import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import type { flows } from '@repletoryreach/sdk-api';
import { useAppStore } from '@repletoryreach/sdk-utils/store';
import { useI18n } from '../../i18n/I18nProvider';
import { LOCALES } from '../../i18n/catalog';
import { NAV, adminOnlyPrefixes } from '../../lib/nav';
import { useVoiceStatus } from '../../lib/voice';
import { useCredits } from '../../lib/credits';
import { useWallet } from '../../data/useWallet';
import { useIsOrgAdmin } from '../components/RoleGate';
import { GUIDES } from '../guides/registry';
import { guideFor } from '../guides/GuideProvider';
import { CONSOLE_PAGES } from './console-map';

/** The most guide text sent with one message, so a long guide does not make every question dear. */
const GUIDE_CHARS = 6000;

/** The map page a path is on: an exact page, or a page with an id (`/contacts/:id`). */
export function pageFor(pathname: string) {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return CONSOLE_PAGES.find((p) => p.path === clean)
    ?? CONSOLE_PAGES.find((p) => p.path.includes(':') && new RegExp(`^${p.path.replace(/:[^/]+/g, '[^/]+')}$`).test(clean));
}

/**
 * What the console tells the assistant with each message (8 October 2026): who is signed in, the page they are on
 * and its module's written guide, the pages they may open (agents never see owner-and-admin pages), the guides, the
 * languages and the look of the console, and the credit balance.
 */
export function useAssistantContext(): () => flows.ConsoleAssistantContext {
  const { pathname, search } = useLocation();
  const { user } = useAuth();
  const { t, locale } = useI18n();
  const isAdmin = useIsOrgAdmin();
  const { available: voiceAvailable } = useVoiceStatus();
  const credits = useCredits();
  const { data: wallet } = useWallet();
  const theme = useAppStore((s) => s.themeMode);
  const density = useAppStore((s) => s.density);

  return useCallback(() => {
    const blocked = isAdmin ? [] : adminOnlyPrefixes();
    const navTitle = new Map(NAV.flatMap((s) => s.items).map((i) => [i.path, t(i.labelKey, i.label)]));
    const pages = CONSOLE_PAGES
      .filter((p) => p.navigable !== false)
      .filter((p) => !blocked.some((b) => p.path === b || p.path.startsWith(`${b}/`)))
      .filter((p) => p.path !== '/voice' || voiceAvailable)
      .map((p) => ({ path: p.path, title: navTitle.get(p.path) ?? p.title, about: p.about }));
    const guide = guideFor(pathname);
    const guideText = guide
      ? [t(guide.titleKey), t(guide.introKey), ...guide.sections.flatMap((s) => [
          `## ${t(s.titleKey)}`, ...s.bodyKeys.map((k) => t(k)), ...(s.noteKeys ?? []).map((k) => `Note: ${t(k)}`)
        ])].join('\n').slice(0, GUIDE_CHARS)
      : undefined;
    const here = pageFor(pathname);
    const w = wallet?.data;
    return {
      path: pathname + search,
      page_title: here ? navTitle.get(here.path) ?? here.title : undefined,
      user_name: user?.display_name || user?.email,
      business_name: user?.org?.name,
      language: locale,
      theme,
      density,
      credits: w ? credits(w.credits_minor, w.currency) : undefined,
      pages,
      guides: Object.values(GUIDES).map((g) => ({ id: g.id, title: t(g.titleKey) })),
      languages: LOCALES.map((l) => l.code),
      page_guide: guideText
    };
  }, [pathname, search, user, t, locale, isAdmin, voiceAvailable, credits, wallet, theme, density]);
}
